"use client";

import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { buildFrontendUrl } from "@/lib/billing/frontend-urls";
import { createStripePortalSession } from "@/lib/billing/stripe";

type UseStripePortalResult = {
  isRedirecting: boolean;
  error: ReactNode | null;
  openPortal: (returnPath?: string, openInNewTab?: boolean) => Promise<void>;
};

export const useStripePortal = (): UseStripePortalResult => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<ReactNode | null>(null);

  const openPortal = useCallback(async (returnPath?: string, openInNewTab = true) => {
    setError(null);
    setIsRedirecting(true);
    const portalWindow = openInNewTab ? window.open("about:blank", "_blank") : null;
    if (portalWindow) portalWindow.opener = null;

    try {
      const returnUrl = buildFrontendUrl(returnPath, "/manage/billing");
      const { url } = await createStripePortalSession(returnUrl);
      if (portalWindow) {
        portalWindow.location.assign(url);
      } else {
        window.location.assign(url);
      }
    } catch (err) {
      console.error(err);
      portalWindow?.close();
      setError(
        <>
          Your billing account may still be setting up. Please check back in a minute or two. If the problem persists,{" "}
          <a href="mailto:support@radium.cloud" className="underline">
            contact support
          </a>
          .
        </>,
      );
      setIsRedirecting(false);
    }
  }, []);

  return { isRedirecting, error, openPortal };
};
